import { computed, effectScope, ref, watch } from "vue"
import { api } from "boot/axios"
import { globalTenantIdRef, globalTenantRecursiveRef } from "src/stores/tenant.js"

// useFeatureGate is the SPA's single source of truth for license-driven
// feature gating. It backs the upsell UX: tier badges on entry-point
// cards, "upgrade required" placeholder pages, and the inline domain-
// add warning. Mirrors the contract emitted by GET /v1/logged-user/features
// (see restapi/loggeduserapi/features.go) which returns:
//
//   { "<feature-name>": { enabled, licensed, tier } }
//
// The two booleans intentionally answer different questions:
//
//   - enabled  = role ∩ license. The single source of truth for "can
//                this user actually use the working feature surface
//                right now?". Drives `tm-if-permission`-style hides
//                where appropriate.
//   - licensed = does the scoped tenant own the feature in their
//                plan, regardless of the caller's role? The signal
//                that distinguishes a "you don't have the role"
//                gate (silent) from a "you don't have the license"
//                gate (upsell teaser + contact-us CTA).
//
// State is module-level (not per-call) so every component shares one
// fetched copy and no two callers thrash the endpoint. The fetch is
// re-run automatically when the global tenant picker changes - feature
// gates are scope-sensitive (an MSP ScopED into a licensed sub-tenant
// must see the feature even when their own home segment lacks it,
// per featuregate.IsEnabledForRequestScope on the backend).

const features = ref({})
const loading = ref(false)
const loaded = ref(false)
const lastError = ref(null)

// Monotonically-increasing request id. Each fetchFeatures invocation
// gets a fresh id and the response handler only commits its result if
// the id still matches the latest one. This is the correctness
// backbone of the cache-and-refetch model: the global tenant watcher
// fires both at install (with whatever tenantId is - often null while
// the picker is still hydrating from local storage / login) and again
// when the picker actually lands. Without sequence guarding, the
// later "real" fetch could finish first OR get silently merged with
// the earlier one, leaving the cache stuck on the home segment's
// plan even though the user is actively scoped into a different
// (licensed or unlicensed) sub-tenant. That manifested in production
// as the Deep Dark Web Report's upgrade-hero gate firing for tenants that
// DO own ARMOUR, simply because their home segment doesn't.
let reqSeq = 0
let watcherInstalled = false

async function fetchFeatures() {
	const myReq = ++reqSeq
	loading.value = true
	lastError.value = null
	try {
		const resp = await api.get("/v1/logged-user/features")
		// Stale-response guard: only the latest in-flight fetch
		// commits its result. A faster older response that lost
		// the race must be discarded so the cache reflects the
		// picker's current scope.
		if (myReq !== reqSeq) return
		features.value = resp?.data || {}
		loaded.value = true
	} catch (err) {
		if (myReq !== reqSeq) return
		// Failures are non-fatal: the SPA falls back to the
		// pre-existing `tm-if-permission` checks against
		// localStorage user_permissions. We log but don't toast
		// because this endpoint backs ambient nav decoration -
		// a noisy toast on every tenant switch would be worse
		// than silently degrading to permission-only gating.
		lastError.value = err?.message || "unknown"
		console.warn("useFeatureGate: failed to load /v1/logged-user/features", err)
	} finally {
		// Only the latest fetch flips the global loading flag back
		// off. An older fetch finishing while a newer one is still
		// in flight must NOT clear the spinner - the cache is still
		// catching up to the picker's current tenant.
		if (myReq === reqSeq) loading.value = false
	}
}

// installTenantWatcher wires the module-level fetch to the global
// tenant picker so the upsell UX stays accurate as the user moves
// between tenants OR toggles the "Include Sub-Tenants" checkbox.
// Idempotent — only the first caller installs the watcher;
// subsequent callers piggyback on the same shared state.
//
// We watch BOTH globalTenantId and globalTenantRecursive because the
// features endpoint is scope-aware on the backend
// (loggeduserapi.licenseScopeSegmentID + featuregate.IsEnabledForRequestScope).
// When the user is scoped into a tenant whose parent has a different
// license footprint than the sub-tree (e.g. MSP parent without ARMOUR
// but a child with ARMOUR), toggling the checkbox flips the answer
// the backend would give. Without re-fetching on the recursive flag,
// the upsell UX would stay frozen on the previous scope's plan -
// either flashing the upgrade hero on a tenant that DOES own the
// feature, or hiding it on a tenant that doesn't. Both manifest as
// "the report won't render the right thing when I toggle the box".
function installTenantWatcher() {
	if (watcherInstalled) return
	watcherInstalled = true
	const tenantId = globalTenantIdRef()
	const tenantRecursive = globalTenantRecursiveRef()
	// Detached scope. useFeatureGate() is always first called from a route
	// component's setup() - no layout or boot file calls it - so a bare
	// watch() would bind to that component's effect scope and be stopped
	// when it unmounts. `watcherInstalled` then blocks any re-install, so
	// the cache would freeze on whichever tenant was current at that
	// moment and every later picker change would be ignored: a licensed
	// tenant stuck behind an upgrade hero, or a gated one shown live
	// controls. Detaching keeps the watcher alive for the session.
	effectScope(true).run(() => {
		watch(
			[tenantId, tenantRecursive],
			([newId]) => {
				// Skip the watcher's first fire when the picker is still
				// hydrating (tenant ref starts as null and MainLayout
				// resolves it asynchronously from local storage). A
				// fetch with rootSegmentId=null would land on the user's
				// home segment instead of the picked one and briefly
				// flip `loaded=true` with the wrong tenant's plan -
				// enough to flash the upgrade hero on a licensed
				// sub-tenant before the real fetch corrects it. The
				// follow-up tenant-change event triggers the real fetch
				// once the picker actually lands.
				if (!newId) return
				loaded.value = false
				fetchFeatures()
			},
			{ immediate: true },
		)
	})
}

export default function useFeatureGate() {
	installTenantWatcher()

	function statusFor(name) {
		return features.value?.[name] || null
	}

	// isEnabled answers "can the user use this feature right now?"
	// (role ∩ license). False = hide the working surface.
	function isEnabled(name) {
		return !!statusFor(name)?.enabled
	}

	// isLicensed answers "does the tenant own this feature in their
	// plan?" (license-only, role-independent). The single signal the
	// upsell UX branches on - when false, the SPA shows the upgrade
	// teaser and tier badge; when true but isEnabled is false, the
	// gate is the user's role (no upsell, just hide).
	function isLicensed(name) {
		return !!statusFor(name)?.licensed
	}

	// isFreePreview answers "does the tenant have this feature only
	// because a free-access window is open?". True = they can use it
	// today but have not bought it.
	//
	// This is NOT derivable from the other two. A free window is
	// injected at license RESOLUTION on the backend - deliberately, so
	// that every access check honours it with one switch - which means
	// `enabled` and `licensed` both read true for a Shield tenant
	// inside the window, exactly as they do for an Armour tenant that
	// owns the feature. Only the backend can tell them apart, and this
	// is the flag it sends for the purpose.
	//
	// Use it for badging, never for gating: the surface is genuinely
	// available in both cases, so hiding anything on this would take
	// the preview away from the tenants it exists for.
	//
	// Guarded on loaded/lastError like showUpgrade, but failing the
	// other way — CLOSED. The tenant watcher clears `loaded` without
	// clearing `features`, so a bare read of the cache serves the
	// PREVIOUS tenant's answer for the length of the refetch, and
	// indefinitely if that refetch fails (lastError is set, features
	// is left alone). Unguarded, an MSP operator moving the picker
	// from a Shield sub-tenant to an Armour one gets "Free Preview"
	// stamped on a feature the new tenant paid for — the exact
	// mis-badge the backend fails closed to avoid. A badge has no
	// fail-open hazard: showing nothing while we don't know costs
	// nothing.
	function isFreePreview(name) {
		if (lastError.value) return false
		if (!loaded.value) return false
		return !!statusFor(name)?.freePreview
	}

	// tier returns the marketing-plan slug ("armour") so callers can
	// render a tier badge / tailor copy without baking feature names
	// into the frontend. Empty string = baseline plan.
	function tier(name) {
		return statusFor(name)?.tier || ""
	}

	// needsUpgrade is the convenience predicate for the upsell UX:
	// true when the tenant's plan doesn't include the feature, so the
	// SPA should prompt billing. False when the feature is licensed
	// (regardless of whether the user's role currently allows it -
	// that's an internal-RBAC issue, not a billing one).
	function needsUpgrade(name) {
		const status = statusFor(name)
		if (!status) return false
		return !status.licensed
	}

	// showUpgrade is the single predicate every gated surface branches
	// on: "replace the working surface with the upsell teaser right
	// now?". It layers the two fail-open rules on top of needsUpgrade
	// so no call site has to re-derive them (and get them subtly
	// different):
	//
	//   - Not loaded yet → false. The cache starts empty and the
	//     tenant watcher only fires once the picker lands, so a
	//     truthy answer here would flash the upgrade hero on every
	//     licensed tenant for the duration of the first fetch.
	//   - Transport error → false. A failed /v1/logged-user/features
	//     must never yank a working feature away from a licensed
	//     user mid-session; the pre-existing permission checks stay
	//     as the fallback gate, and a genuine 403 from the data
	//     endpoint is what the page falls back to.
	//
	// An unknown feature name also yields false (via needsUpgrade),
	// so a SPA deployed ahead of a backend that doesn't emit the key
	// keeps showing the working surface.
	function showUpgrade(name) {
		if (lastError.value) return false
		if (!loaded.value) return false
		return needsUpgrade(name)
	}

	return {
		features: computed(() => features.value),
		loading: computed(() => loading.value),
		loaded: computed(() => loaded.value),
		lastError: computed(() => lastError.value),
		// resolved: the license answer has landed, or its fetch failed. A page
		// that must not act on showUpgrade's fail-open "no" (because acting
		// would fire requests an unlicensed tenant can only 403 on) waits for
		// this, then lets a failed fetch fall through to the server's answer.
		resolved: computed(() => loaded.value || !!lastError.value),
		statusFor,
		isEnabled,
		isLicensed,
		isFreePreview,
		tier,
		needsUpgrade,
		showUpgrade,
		refresh: fetchFeatures,
	}
}

// resetFeatureGate drops the cached licence answers. Call it on logout.
//
// The cache is module-level on purpose (one copy, one fetch), which also means
// it outlives the session: without this, logging out and back in as a different
// user in the same tab leaves every gated surface reading the PREVIOUS
// account's plan until the tenant picker lands and the watcher refetches. That
// window badges — or worse, un-badges — surfaces off someone else's licence.
//
// Bumping reqSeq is part of the reset, not incidental: a fetch already in
// flight for the old session would otherwise still pass its own
// `myReq === reqSeq` check and commit the old tenant's plan into the fresh one.
export function resetFeatureGate() {
	reqSeq++
	features.value = {}
	loading.value = false
	loaded.value = false
	lastError.value = null
}

// isFeatureGateError reports whether an axios error is a license-gate
// 403 emitted by featuregate, optionally narrowed to a specific feature.
//
// Two wire shapes, and the SECOND one is the live one:
//
//   1. featuregatehttp.FeatureNotEnabledResponse —
//      {"message":"Feature not enabled: <name>", "feature":"<name>"}.
//      Only its RequireFeature route filter emits this, and nothing on the
//      backend mounts that filter today. Matched first because it is exact
//      and survives a rewording of the prose.
//   2. restfulwrapper.APIResponseErrorOutput —
//      {"type":"...","message":"Feature not enabled: <name>"}, no `feature`
//      key. THIS is what every live denial returns: assetapi/asset-pii.go,
//      assetapi/asset-id-pii.go, securityscoreapi/prospectingscoreapi.go,
//      agentapi/agent-id-task.go and all of deepdarkwebapi build it with
//      restfulwrapper.NewAPIResponseError(403, "Feature not enabled: ...").
//
// So the message branch below is not legacy — it is the only path that
// currently fires, and every gate in the SPA rests on that exact prose.
// Reword the Go string and the pitches silently become raw 403 toasts.
// (It parses the name out and compares it exactly, so a future
// "crown-jewels-discovery" denial is not read as "crown-jewels".)
//
// Gated pages use this to tell a license 403 apart from an RBAC 403 on
// the same endpoint: the former renders the upgrade teaser silently,
// the latter still surfaces the error toast it always did. It also
// covers the window before /v1/logged-user/features resolves, when
// showUpgrade() is still deliberately false and the data request is
// the first thing to learn the tenant isn't licensed.
export function isFeatureGateError(err, name = "") {
	if (err?.response?.status !== 403) return false
	const data = err?.response?.data
	if (typeof data?.feature === "string" && data.feature !== "") {
		return name ? data.feature === name : true
	}
	// The live path. Parse the name out and compare exactly: a substring test
	// would read a "crown-jewels-discovery" denial as "crown-jewels" and show
	// the wrong pitch.
	const message = data?.message
	const prefix = "Feature not enabled: "
	if (typeof message !== "string" || !message.startsWith(prefix)) return false
	const denied = message.slice(prefix.length).trim()
	return name ? denied === name : denied !== ""
}

// Stable feature-name constants. Keep in sync with the Feature.Name
// values declared in internal/featuregate/features.go - these strings
// are the wire contract.
export const FEATURE_DARK_WEB_ENHANCED = "deep-dark-web"

// Crown Jewels (endpoint sensitive-data discovery) and Automated
// Remediation (hands-off CVE-gated patching) are the two features the
// Armour cutoff brought under the license. Both are enforced on the
// backend from the tenant's customerlicense alone — the
// FEATURE_FLAG_ARMOR_ENFORCE_* kill switches that once held them
// ungated were removed in threatmate#4073, so /v1/logged-user/features
// now reports licensed per tenant and the gates below are live. An
// unlicensed tenant gets the matching upgrade placeholder; a tenant
// gains or loses the feature when its license changes, with no SPA
// deploy involved.
export const FEATURE_CROWN_JEWELS = "crown-jewels"
export const FEATURE_AUTOMATED_REMEDIATION = "automated-remediation"

// Config Hardening (Windows configuration-baseline evaluation). Armour-tier
// like the three above, enforced from the tenant's customerlicense alone.
export const FEATURE_ENDPOINT_HARDENING = "endpoint-hardening"

// Shadow AI Discovery (AI/SaaS usage inventory, the browser sensor, the AI Risk
// Report). Armour-tier, enforced from the tenant's customerlicense alone.
export const FEATURE_SHADOW_AI = "shadow-ai"

// Stable tier constants. Keep in sync with internal/featuregate/feature.go
// (Tier* constants).
export const TIER_ARMOUR = "armour"

// TIER_PREVIEW is not a plan — it is the badge a feature wears while it is
// temporarily included for everyone (a free-access window). It has its own
// TmTierBadge style rule, so it is a real tier everywhere except billing;
// giving it a constant and a TIER_LABELS entry is what keeps the slug and the
// label out of call sites.
export const TIER_PREVIEW = "preview"

// TIER_LABELS maps a tier slug to the customer-facing label we show in
// badges and upsell copy. Centralized here so a marketing rename only
// touches one place.
//
// The ARMOUR tier displayed as "FREE PREVIEW" while the pre-cutoff
// rebrand was live, so the badge read "this is included for free right
// now." That window has closed — Crown Jewels and Automated Remediation
// are enforced from the license — so the label is back to the plan name
// it upsells. The orange-gradient styling in TmTierBadge is unchanged;
// only the text inside the badge swapped back.
export const TIER_LABELS = {
	[TIER_ARMOUR]: "ARMOUR",
	[TIER_PREVIEW]: "Free Preview",
}

// FEATURE_UPGRADE_PITCH is the canonical upsell copy for each gated
// feature. Centralized here so the in-page TmUpgradePlaceholder hero
// and the click-gate TmUpgradeModal stay in sync — and so a marketing
// copy edit only touches one file.
//
// Each entry mirrors the prop surface of TmUpgradePlaceholder.vue, so
// callers can pass the resolved pitch straight through. Keep entries
// short (3–5 benefits) and feature-value-led, not gate-led.
export const FEATURE_UPGRADE_PITCH = {
	[FEATURE_DARK_WEB_ENHANCED]: {
		title: "Deep Dark Web Insights",
		subtitle: "Live cross-signal detection across credential leaks, infostealer logs, combo lists, active session cookies, look-alike phishing domains, and threat-actor chatter for every user and domain in this tenant. Findings are persisted, alerted, and folded into the Deep Dark Web Report automatically.",
		icon: "tm:dark-web-insights",
		tier: TIER_ARMOUR,
		benefits: [
			{ title: "Daily threat-feed coverage", detail: "Continuous ingestion of credential dumps, infostealer logs, combo lists, and session cookies; per-domain scans for look-alike phishing and threat-actor chatter on top." },
			{ title: "Findings flow into the Deep Dark Web Report", detail: "New creds, stealer logs, combo entries, and session cookies render in the next report without any extra setup." },
			{ title: "Alerts fire on discovery", detail: "Severity-tagged alerts hit the standard pipeline (email digests, Slack, mission plan) the moment new data lands." },
			{ title: "Truncation-safe paging", detail: "Default caps protect quota; segment admins can expand the page depth when chasing a high-noise account." },
		],
		footer: "",
		secondaryRoute: "",
		secondaryLabel: "",
		secondaryIcon: "",
	},
	[FEATURE_CROWN_JEWELS]: {
		title: "Crown Jewels",
		subtitle: "Find where your sensitive data actually lives. The agent scans each enrolled endpoint for PII, financial records, and stored credentials, scores every device by what it holds, and turns the riskiest files into findings you can act on.",
		icon: "tm:crown-jewels",
		tier: TIER_ARMOUR,
		benefits: [
			{ title: "Sensitive data discovery on every endpoint", detail: "Weekly scans of the directories that actually hold data — Desktop, Documents, Downloads, and synced cloud storage — with per-file evidence and redacted samples." },
			{ title: "Crown Jewel scoring per device", detail: "Every device gets a score from what it stores, so you can tell a finance workstation apart from a kiosk without opening a single file." },
			{ title: "Compliance framework mapping", detail: "Findings carry the frameworks each data type maps to, so a discovery doubles as evidence for the audit you're already preparing." },
			{ title: "Findings and alerts, not just a report", detail: "Discoveries land in the standard findings pipeline with severity, so they route through your existing alerts, digests, and mission plan." },
		],
		footer: "",
		secondaryRoute: "/help/pii-scanner",
		secondaryLabel: "Learn how Crown Jewels works",
		secondaryIcon: "tm:help",
	},
	[FEATURE_AUTOMATED_REMEDIATION]: {
		title: "Automated Remediation",
		subtitle: "Let ThreatMate keep your Windows endpoints patched on their own. Third-party security updates install on the patch schedule with no operator in the loop — only ever an update that fixes a known CVE at or above the severity bar you set.",
		icon: "tm:auto-remediation",
		tier: TIER_ARMOUR,
		benefits: [
			{ title: "Hands-off third-party patching", detail: "Approved updates install on each device's patch schedule without anyone clicking through a queue." },
			{ title: "CVE-gated, with a severity threshold you set", detail: "Nothing installs unless it fixes a known vulnerability at or above your bar — blending exploit likelihood with impact." },
			{ title: "Change control that fits your maintenance window", detail: "Confine automatic installs to off-hours, exclude the packages you patch by hand, and stage rollout with remediation rings." },
			{ title: "Per-device opt-in", detail: "The tenant switch is the master; each device still has to be turned on, so you can start with a pilot group and widen from there." },
		],
		footer: "",
		secondaryRoute: "/remediation",
		secondaryLabel: "Use Guided Remediation",
		secondaryIcon: "tm:patch",
	},
	// Every Config Hardening lock — the settings rows, the device capability,
	// the devices-table icon, the fleet page and the three reports — opens
	// this. Without an entry they would all be inert locks, which is precisely
	// what LICENSE_PERMISSION_FEATURES exists to prevent.
	[FEATURE_ENDPOINT_HARDENING]: {
		title: "Config Hardening",
		subtitle: "See how each Windows endpoint is actually configured, not just what is installed on it. The agent reads every device's security and audit policy, scores it against the hardening baseline, and turns each failing control into a finding with the exact fix.",
		icon: "tm:config-hardening",
		tier: TIER_ARMOUR,
		benefits: [
			{ title: "Every device scored against the baseline", detail: "Password and lockout policy, user-rights assignment, audit policy, firewall profiles and the security options behind them — checked on each inventory scan, read-only." },
			{ title: "Posture across every device, not a per-device hunt", detail: "One view of which controls fail on how many devices, with the trend over time, so you fix the control once instead of chasing it device by device." },
			{ title: "Fix guidance on every failing control", detail: "Each finding carries what was expected, what was found, and the change that closes it." },
			{ title: "Evidence your auditor will take", detail: "Acknowledge a control with a justification that reaches the score, and export every device's results as the CSV an audit asks for." },
		],
		footer: "",
		secondaryRoute: "/help/config-hardening",
		secondaryLabel: "Learn how Config Hardening works",
		secondaryIcon: "tm:help",
	},
	[FEATURE_SHADOW_AI]: {
		title: "Shadow AI Discovery",
		subtitle: "See which AI tools your team actually uses: installed apps, Microsoft 365 OAuth grants, and in-browser use in Chrome and Edge on Windows. With a sanction policy and an AI Risk Report ready to hand over.",
		icon: "mdi-robot-outline",
		tier: TIER_ARMOUR,
		benefits: [
			{ title: "One inventory across every source", detail: "Installed AI apps, OAuth-granted access to company data, and in-browser use, de-duplicated into one line per tool with reach, engagement and account facts." },
			{ title: "Set a policy and see who is outside it", detail: "Mark each tool approved, tolerated or unsanctioned, and see exactly which devices and users are running the ones you have not sanctioned." },
			{ title: "The AI Risk Report", detail: "The QBR and insurance-renewal deliverable: what is in use, the exposure, and what to do about it — with what the sensor does and does not read stated on the page." },
			{ title: "Usually no GPO work", detail: "Turn the browser sensor on per device or per tenant and the agent installs and configures the extension for you on Windows." },
		],
		footer: "The free preview of Shadow AI Discovery has ended.",
		secondaryRoute: "/help/shadow-ai",
		secondaryLabel: "Learn how Shadow AI Discovery works",
		secondaryIcon: "tm:help",
	},
}

// getFeatureUpgradePitch returns the canonical upsell metadata for a
// feature, or null if no pitch is registered. Returns a shallow copy
// so callers can apply per-call overrides without mutating the global
// registry.
export function getFeatureUpgradePitch(name) {
	const pitch = FEATURE_UPGRADE_PITCH[name]
	if (!pitch) return null
	return { ...pitch }
}

// LICENSE_PERMISSION_FEATURES maps the `requiredPermission` the settings API
// stamps on a licence-locked row to the feature whose pitch that row belongs
// to. One table: it had grown a copy per consumer (StandardSettingRows,
// SegmentSettingsTable, plus a parallel permission->tier map in
// useSegmentSettings), so gating a fourth setting meant remembering all of
// them, and missing one left a row whose badge and whose pitch named
// different plans.
export const LICENSE_PERMISSION_FEATURES = {
	"crown-jewels:read": FEATURE_CROWN_JEWELS,
	"automated-remediation:read": FEATURE_AUTOMATED_REMEDIATION,
	"deep-dark-web:read": FEATURE_DARK_WEB_ENHANCED,
	"endpoint-hardening:read": FEATURE_ENDPOINT_HARDENING,
	"shadow-ai:read": FEATURE_SHADOW_AI,
}

// featureForPermission returns the feature a locked row belongs to, or "" when
// the permission isn't one we can pitch (the backend locks rows generically
// from RequiresPermission, so this is an expected case). Call sites MUST treat
// "" as "not clickable" — a lock badge wired to a dialog it cannot open is the
// inert gated control useUpgradeDialog exists to prevent.
export function featureForPermission(permission) {
	return LICENSE_PERMISSION_FEATURES[permission] || ""
}

// tierForPermission resolves the marketing tier behind a locked row through
// the same pitch registry the badges use, so a row's lock badge and the pitch
// its click opens can never name two different plans.
export function tierForPermission(permission) {
	const feature = featureForPermission(permission)
	return feature ? (FEATURE_UPGRADE_PITCH[feature]?.tier || "") : ""
}
